/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import {
  Typography,
  useTheme,
  Stack,
  Button,
  Box,
  LinearProgress,
} from "@mui/material";
import { PublishIcon, TrashIcon, DownloadIcon } from "~components/Icons";
import {
  InputFile,
  Headline,
  SubTitle,
  CustomModal,
  OverlayDragAndDrop,
  BrandSelector,
} from "~components/atoms";
import { ProductsGrid } from "~components/molecules";
import { CategoryTabs, ProductSearch } from "~components/Organisms";
import { useStreamContext } from "~contexts/Stream";
import { useSocketContext } from "~contexts/Socket";
import { useProductsContext } from "~contexts/Products";
import { useToastNotificationContext } from "~contexts/ToastNotification";
import { authFetch } from "~helpers/authFetch";
import { getStyles as getModalStyles } from "~components/atoms/CustomModal/styles";
import useCronJob from "../../../hooks/useCronJob";

const toastMessages = {
  success: {
    title: "informações salvas",
    text: "Atualização das categorias realizada com sucesso",
    cron: {
      title: "informações salvas",
      text: "Atualização da planilha realizada com sucesso",
    },
  },
  error: {
    title: "falha na requisição",
    text: "Problemas na requisição para atualizar categorias",
  },
};

const toastMessagesCron = {
  success: {
    title: "Atualização",
    text: "Atualização da planilha realizada com sucesso",
  },
  error: {
    title: "falha na requisição",
    text: "Problemas na  atualização da planilha ",
  },
};

const ProductsPage = () => {
  const theme: any = useTheme();
  const { modal: modalStyles } = getModalStyles(theme);
  const [isModalOpened, setModalOpened] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isFileOnDropzone, setFileOnDropZone] = useState<boolean>(false);
  const [preloadErrors, setPreloadErrors] = useState<
    { categoryId: number; attempts: number; message: string }[]
  >([]);
  const { setToastData } = useToastNotificationContext();
  const { loading, stream, currentStream } = useStreamContext();
  const { updateStreamSocket } = useSocketContext();
  const { refreshCronJobs, responseApi } = useCronJob();

  const {
    buttons,
    setButtons,
    buttonActive,
    productList,
    setProductList,
    deleteButton,
    setDroppedFile,
    loadingProducts,
    clearAllCache,
    selectCategoryFromCache,
    seedCacheFromStreamButtons,
    notFoundInVTEX,
    notFoundProductIds,
    loadedProductsFromSheet,
    getCategoryProductsFromCache,
    getCategoryNotFoundProductIds,
  } = useProductsContext();

  const [isNotFoundModalOpened, setNotFoundModalOpened] = useState<boolean>(false);
  const activeWorkersRef = useRef<Set<{ stop: boolean }>>(new Set());

  useEffect(() => {
    if (responseApi === "refresh") {
      if (currentStream) {
        updateStreamSocket(currentStream as any);
      }
      setToastData(toastMessagesCron.success, "success");

      setTimeout(() => {
        window.location.reload();
      }, 3000);
    }
  }, [responseApi, setToastData]);

  useEffect(() => {
    // Quando currentStream muda, sempre limpar produtos e botões anteriores primeiro
    if (currentStream) {
      const streamId = currentStream.id;
      const currentButtonsId = buttons.length > 0 ? buttons[0]?.id_stream : null;

      // Se mudou de stream, limpar tudo imediatamente
      if (currentButtonsId !== streamId && currentButtonsId !== null) {
        // Parar todos os workers ativos
        activeWorkersRef.current.forEach((worker) => {
          worker.stop = true;
        });
        activeWorkersRef.current.clear();

        clearAllCache();
        setProductList([]);
        setButtons([]);
        setPreloadErrors([]);
      }
    } else {
      // Se não há currentStream, limpar tudo
      // Parar todos os workers ativos
      activeWorkersRef.current.forEach((worker) => {
        worker.stop = true;
      });
      activeWorkersRef.current.clear();

      clearAllCache();
      setProductList([]);
      setButtons([]);
      setPreloadErrors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStream?.id]);

  useEffect(() => {
    if (!loading && stream && currentStream) {
      const streamId = currentStream.id;
      const currentButtonsId = buttons.length > 0 ? buttons[0]?.id_stream : null;
      const isSameInitializedStream = currentButtonsId === streamId && buttons.length > 0;

      if (isSameInitializedStream) return;

      activeWorkersRef.current.forEach((worker) => { worker.stop = true; });
      activeWorkersRef.current.clear();

      clearAllCache();

      if (stream.buttons && stream.buttons.length > 0) {
        const orderedButtons = [...stream.buttons].sort((a, b) => parseFloat(a.x) - parseFloat(b.x));
        setButtons(orderedButtons);
        setPreloadErrors([]);

        // Produtos já vêm completos no stream — popular cache e exibir primeira categoria
        seedCacheFromStreamButtons(orderedButtons);

        if (orderedButtons[0]?.id_button) {
          selectCategoryFromCache(orderedButtons[0].id_button);
        }
      } else {
        setButtons([]);
        setProductList([]);
        setPreloadErrors([]);
      }
    } else if (!loading && currentStream && (!stream || !stream.buttons || stream.buttons.length === 0)) {
      setButtons([]);
      setProductList([]);
      setPreloadErrors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, loading, currentStream]);

  useEffect(() => {
    // Ao trocar de categoria, usar cache local já carregado nesta sessão.
    if (buttons.length > 0 && buttons[buttonActive]?.id_button && currentStream) {
      const categoryId = buttons[buttonActive].id_button;
      selectCategoryFromCache(categoryId);
    } else if (buttons.length === 0) {
      setProductList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonActive, currentStream]);

  // Se não há brand selecionada, mostrar mensagem (depois de todos os hooks)
  if (!currentStream) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          minHeight: "60vh",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            color: theme.palette.grey[0],
          }}
          variant="h1"
        >
          bem-vindo ao ambiente AzzasLive
        </Typography>
        <Typography
          sx={{
            color: theme.palette.grey[600],
            fontSize: "15px",
            marginBottom: "24px",
          }}
        >
          comece a configurar sua live pelo menu ao lado após selecionar uma marca
        </Typography>
        <BrandSelector invertColors={true} />
      </Box>
    );
  }

  const onExportSheet = () => {
    if (!buttons.length) return;

    const csvRows: string[] = [];
    // Cabeçalho
    csvRows.push(["ID", "Categoria", "Encontrado na VTEX"].join(";"));

    buttons.forEach((button) => {
      const categoryId = button.id_button;
      const categoryLabel = button.label || "";
      if (!categoryId) return;

      const productsFromCache = getCategoryProductsFromCache(categoryId) || [];
      const notFoundIds = getCategoryNotFoundProductIds(categoryId) || [];

      // Produtos encontrados na VTEX
      productsFromCache.forEach((product) => {
        const id = String(product.provider?.productId ?? product.id ?? "").trim();
        if (!id) return;
        csvRows.push([id, categoryLabel, "sim"].join(";"));
      });

      // Produtos não encontrados na VTEX
      notFoundIds.forEach((id) => {
        const trimmedId = String(id || "").trim();
        if (!trimmedId) return;
        csvRows.push([trimmedId, categoryLabel, "não"].join(";"));
      });
    });

    if (csvRows.length <= 1) return; // só cabeçalho, nada pra exportar

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    const filename = currentStream
      ? `produtos-${currentStream.name}.csv`
      : "produtos.csv";
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const SECONDARY_BUTTON = {
    icon: <PublishIcon />,
    text: "publicar",
    onAction: () => setModalOpened(true),
  };

  const PRIMARY_BUTTON = {
    icon: <DownloadIcon />,
    text: "exportar planilha",
    onAction: onExportSheet,
  };

  const onDeleteCategory = async () => {
    const deleted = await deleteButton();
    if (!deleted) {
      setToastData(toastMessages.error, "error");
      return;
    }

    if (currentStream) {
      updateStreamSocket({ categoriesUpdated: true, productsUpdated: true });
    }
  };

  const onPublish = async () => {
    const streamId = currentStream.id;
    setLoading(true);
    await authFetch({
      path: `${process.env.REACT_APP_BACKEND_URL}/stream/${streamId}/categories`,
      method: "PUT",
      data: buttons,
    })
      .then((res) => {
        setToastData(toastMessages.success, "success");
        setModalOpened(false);
        return res.json();
      })
      .catch(() => setToastData(toastMessages.error, "error"))
      .finally(() => {
        setLoading(false);
      });
  };

  const onDragOverAndEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    const isValidType =
      e.dataTransfer.items[0].type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      e.dataTransfer.items[0].type === "xlsx";

    if (isValidType) {
      setFileOnDropZone(true);
    }
  };

  const dropHandler = (ev: any) => {
    ev.preventDefault();
    ev.stopPropagation();
    setFileOnDropZone(false);

    if (ev.dataTransfer.items) {
      const isValidType =
        ev.dataTransfer.items[0].type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        ev.dataTransfer.items[0].type === "xlsx";
      if (ev.dataTransfer.items[0].kind === "file" && isValidType) {
        const file = ev.dataTransfer.items[0].getAsFile();
        setDroppedFile(file);
      }
    }
  };

  if (!stream || loading) return <>carregando...</>;

  const totalProducts = buttons.reduce((sum, btn) => sum + (btn.quantity || 0), 0);
  const isCatalogLoaded =
    !loadingProducts &&
    preloadErrors.length === 0 &&
    (totalProducts === 0 || loadedProductsFromSheet >= totalProducts);

  return (
    <div
      onDrop={dropHandler}
      onDragOver={onDragOverAndEnter}
      onDragLeave={() => setFileOnDropZone(false)}
    >
      <OverlayDragAndDrop isFileOnDropzone={isFileOnDropzone} />
      <Stack>
        <CustomModal isOpened={isModalOpened} onClose={() => setModalOpened(false)}>
          <Typography style={modalStyles.titleStyle}>
            confirmação de publicação
          </Typography>
          <Typography
            style={{
              ...modalStyles.textStyle,
              color: theme.palette.grey[400],
            }}
          >
            você solicitou publicar as modificações, deseja realmente publicar?
          </Typography>
          <div style={modalStyles.buttonsStyle}>
            <Button
              disableFocusRipple
              disableRipple
              onClick={() => setModalOpened(false)}
              sx={modalStyles.buttonDownloadStyle}
            >
              não, quero cancelar
            </Button>
            <Button
              disableFocusRipple
              disableRipple
              onClick={() => onPublish()}
              style={modalStyles.buttonCancelStyle}
            >
              {isLoading ? "publicando..." : "sim, quero publicar"}
            </Button>
          </div>
        </CustomModal>

        {/* Modal de produtos não encontrados na VTEX */}
        <CustomModal
          isOpened={isNotFoundModalOpened}
          onClose={() => setNotFoundModalOpened(false)}
        >
          <Typography style={modalStyles.titleStyle}>
            produtos não encontrados na VTEX
          </Typography>
          <Typography
            style={{
              ...modalStyles.textStyle,
              color: theme.palette.grey[400],
              marginBottom: "16px",
            }}
          >
            {notFoundInVTEX} produto(s) desta página não foram encontrados na VTEX:
          </Typography>
          <Box
            sx={{
              maxHeight: "400px",
              overflowY: "auto",
              backgroundColor: theme.palette.grey[700],
              borderRadius: "4px",
              padding: "16px",
              marginBottom: "16px",
            }}
          >
            {notFoundProductIds.length > 0 ? (
              <Stack spacing={1}>
                {notFoundProductIds.map((productId, index) => (
                  <Typography
                    key={index}
                    sx={{
                      fontSize: "14px",
                      color: theme.palette.grey[300],
                      fontFamily: "monospace",
                    }}
                  >
                    {productId}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{
                  fontSize: "14px",
                  color: theme.palette.grey[400],
                }}
              >
                Nenhum ID disponível
              </Typography>
            )}
          </Box>
          <div style={modalStyles.buttonsStyle}>
            <Button
              disableFocusRipple
              disableRipple
              onClick={() => setNotFoundModalOpened(false)}
              style={{
                ...modalStyles.buttonCancelStyle,
                backgroundColor: theme.palette.grey[700],
                width: "75px",
                borderRadius: "100px",
                marginLeft: "0px",
              }}
            >
              fechar
            </Button>
          </div>
        </CustomModal>

        <Headline
          id="products"
          title="produtos"
          primaryButton={PRIMARY_BUTTON}
          secondaryButton={SECONDARY_BUTTON}
          description="não esqueça de salvar as alterações antes de sair"
        />
        <SubTitle
          title="lista de produtos"
          description="busque e adicione produtos manualmente ou faça o upload de uma planilha excel"
        />
        <Typography style={{ fontSize: "12px", color: "#858585" }}>
          {" "}
          para baixar uma planilha padrão{" "}
          <a
            style={{ color: "#858585" }}
            href="https://storage.googleapis.com/somalive-public/production/planilhas-cadastro-produtos/farm-aovivo-resultado-cadastro.xlsx"
          >
            clique aqui
          </a>
        </Typography>

        <InputFile />

        <Stack
          direction="row"
          alignItems="center"
          sx={{ mb: "68px", mt: "-40px" }}
          spacing={2}
        >
          <Button onClick={refreshCronJobs} sx={{ p: 0, mr: "5px" }}>
            reenviar planilha
          </Button>
          <img
            alt="reenvio"
            src="https://storage.googleapis.com/somalive-assets/reenvio.svg"
            height={24}
            width={24}
          />
        </Stack>

        {/* Barra de progresso do carregamento de todos os produtos da planilha */}
        {buttons.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {(() => {
              const totalProducts = buttons.reduce(
                (sum, btn) => sum + (btn.quantity || 0),
                0
              );
              if (!totalProducts) return null;

              const loaded = Math.min(loadedProductsFromSheet, totalProducts);
              const progress = (loaded / totalProducts) * 100;
              const isCompleted = loaded >= totalProducts && !loadingProducts;

              return (
                <>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: theme.palette.grey[400],
                      mb: 0.5,
                    }}
                  >
                    {isCompleted
                      ? `carregamento concluído (${totalProducts} items)`
                      : `carregando produtos da planilha (${loaded} de ${totalProducts})`}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Number.isFinite(progress) ? Math.min(progress, 100) : 0}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: isCompleted
                        ? theme.palette.success.light
                        : theme.palette.grey[700],
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: isCompleted
                          ? theme.palette.success.main
                          : theme.palette.warning.main,
                      },
                    }}
                  />
                </>
              );
            })()}
          </Box>
        )}

        {/* Aviso de erro de pré-carregamento de categorias */}
        {preloadErrors.length > 0 && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: theme.palette.error.dark,
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                color: theme.palette.common.white,
                fontWeight: 500,
              }}
            >
              algumas categorias não puderam ser carregadas automaticamente. você ainda
              pode navegar normalmente, mas alguns produtos podem não aparecer. tente
              atualizar a página.
            </Typography>
          </Box>
        )}

        <CategoryTabs />
        <ProductSearch disabled={!isCatalogLoaded} />

        <Stack
          justifyContent="space-between"
          alignItems="center"
          direction="row"
          sx={{ mt: 3 }}
        >
          <Stack direction="column" spacing={0.5}>
            <Typography>
              {buttons[buttonActive]?.quantity !== undefined
                ? `${productList.length} produtos nesta categoria ( de ${buttons[buttonActive].quantity} )`
                : `${productList.length} produtos nesta categoria`}
            </Typography>
            <Box sx={{ minHeight: "20px", display: "flex", alignItems: "center" }}>
              {notFoundInVTEX > 0 && !loadingProducts && (
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: theme.palette.grey[400],
                    cursor: "pointer",
                    textDecoration: "underline",
                    "&:hover": {
                      color: theme.palette.grey[300],
                    },
                  }}
                  onClick={() => setNotFoundModalOpened(true)}
                >
                  {notFoundInVTEX} produtos desta página não encontrados na VTEX
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack direction="column" spacing={0} alignItems="flex-end">
            <Button
              variant="text"
              disableRipple
              disableFocusRipple
              onClick={onDeleteCategory}
              endIcon={<TrashIcon sx={{ color: "transparent" }} />}
              sx={{ mt: 0 }}
            >
              excluir categoria
            </Button>
          </Stack>
        </Stack>

        <ProductsGrid products={productList} page="products" canEdit={isCatalogLoaded} />
      </Stack>
    </div>
  );
};

export default ProductsPage;
